import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  InputAdornment,
  CardHeader,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  Description as DescriptionIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, RELATIONS, getModules, getCreditTypes, getPredefinedActivities, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import { getProfileRecommendation, getModuleGaps, getCareerSuggestions } from '../lib/suggestions';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

export default function StudentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [requests, setRequests] = useState([]);
  const [credits, setCredits] = useState([]);
  const [creditsByCategory, setCreditsByCategory] = useState([]);
  const [moduleCredits, setModuleCredits] = useState([]);
  const [schoolYearHistory, setSchoolYearHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  
  const getSpecializationName = (code) => {
    const specializationsMap = {
      'MI': 'Matematică-Informatică',
    'SN': 'Științe ale Naturii',
    'SS': 'Științe Sociale',
      'FL': 'Filologie',
      'BU': 'Business',
    };
    return specializationsMap[code] || code;
  };

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadAsync = async () => {
      if (id && isMounted && selectedYear) {
        await loadStudentData(isMounted);
      }
    };
    
    loadAsync();
    return () => { isMounted = false; };
  }, [id, selectedYear]);

  // sugestii pentru staff, din datele incarcate
  const studentProfileRec = getProfileRecommendation(student?.specializare);
  const studentGaps = getModuleGaps(moduleCredits, []);
  const studentCareers = getCareerSuggestions(studentProfileRec, moduleCredits);

  const loadStudentData = async (isMounted = true) => {
    setLoading(true);
    try {
      const { data: studentData } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('id', id)
        .single();

      if (!isMounted) return;

      const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];

      
      const { data: modulesData } = await supabase
        .from(TABLES.modules)
        .select('id, cod_module, nume_module, credite_tinta')
        .order('cod_module');

      if (!isMounted) return;

      
      const { data: creditTypesData } = await supabase
        .from(TABLES.credit_types)
        .select('id, nume_tip, categorie');

      if (!isMounted) return;

      
      const { data: requestsData } = await supabase
        .from(TABLES.credit_requests)
        .select(`
          *,
          ${RELATIONS.credit_types}(nume_tip, culoare, categorie),
          ${RELATIONS.modules}(cod_module, nume_module),
          ${RELATIONS.predefined_activities}!left(credite_per_ora, descriere)
        `)
        .eq('id_elev', id)
        .in('an_scolar', yearFilter)
        .order('data_cerere', { ascending: false });

      if (!isMounted) return;

      
      const validatedRequests = requestsData?.filter(r => r.status === 'validat') || [];
      
      
      const creditTypesMap = {};
      creditTypesData?.forEach(ct => {
        creditTypesMap[ct.id] = {
          categorie: ct.categorie || ''
        };
      });

      
      const moduleCreditsMap = {};
      (modulesData || []).forEach(mod => {
        moduleCreditsMap[mod.id] = {
          id: mod.id,
          cod: mod.cod_module,
          nume: mod.nume_module,
          curricular: 0,
          extracurricular: 0,
          transferabil: 0,
          total: 0,
          tinta: mod.credite_tinta || 0
        };
      });

      const creditsCalc = {
        curricular: 0,
        extracurricular: 0,
        transferabil: 0,
      };

      validatedRequests.forEach(req => {
        const typeInfo = creditTypesMap[req.id_tip_credit] || {};
        const categorie = getCreditTypes(req)?.categorie || typeInfo.categorie || '';
        const credite = req.credite_acordate || 0;
        
        const idModul = req.id_module;

        if (categorie === 'curricular') {
          creditsCalc.curricular += credite;
          if (idModul && moduleCreditsMap[idModul]) {
            moduleCreditsMap[idModul].curricular += credite;
            moduleCreditsMap[idModul].total += credite;
          }
        } else if (categorie === 'extracurricular') {
          creditsCalc.extracurricular += credite;
          if (idModul && moduleCreditsMap[idModul]) {
            moduleCreditsMap[idModul].extracurricular += credite;
            moduleCreditsMap[idModul].total += credite;
          }
        } else if (categorie === 'transferabil') {
          creditsCalc.transferabil += credite;
          if (idModul && moduleCreditsMap[idModul]) {
            moduleCreditsMap[idModul].transferabil += credite;
            moduleCreditsMap[idModul].total += credite;
          }
        }
      });

      
      const moduleCreditsArray = Object.values(moduleCreditsMap).sort((a, b) => 
        a.cod.localeCompare(b.cod)
      );

      
      const { data: historyData } = await supabase
        .from(TABLES.student_class_history)
        .select('*')
        .eq('id_elev', id)
        .order('id', { ascending: false });

      if (!isMounted) return;

      setStudent(studentData);
      setRequests(requestsData || []);
      setModuleCredits(moduleCreditsArray);
      setCredits([
        { categorie: 'curricular', credite: creditsCalc.curricular },
        { categorie: 'extracurricular', credite: creditsCalc.extracurricular },
        { categorie: 'transferabil', credite: creditsCalc.transferabil },
      ]);
      setCreditsByCategory([
        { categorie: 'curricular', credite: creditsCalc.curricular },
        { categorie: 'extracurricular', credite: creditsCalc.extracurricular },
        { categorie: 'transferabil', credite: creditsCalc.transferabil },
      ]);
      setSchoolYearHistory(historyData || []);

    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const calculateEstimatedCredits = (request) => {
    if (!request.ore_realizate) return 0;
    
    const creditsPerHour = getPredefinedActivities(request)?.credite_per_ora || 
                         (getCreditTypes(request)?.nume_tip?.toLowerCase() === 'curricular' ? 0.10 : 1.00);
    const estimated = request.ore_realizate * creditsPerHour;
    
    return Math.max(Math.ceil(estimated), 1);
  };

  const getFilteredAndSortedRequests = () => {
    let filtered = [...requests];

    if (filterStatus !== 'all') {
      filtered = filtered.filter(request => request.status === filterStatus);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(request => getCreditTypes(request)?.nume_tip === filterType);
    }

    if (filterModule !== 'all') {
      filtered = filtered.filter(request => getModules(request)?.cod_module === filterModule);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.data_cerere) - new Date(a.data_cerere);
        case 'date_asc':
          return new Date(a.data_cerere) - new Date(b.data_cerere);
        case 'credite_desc':
          const crediteA = a.status === 'validat' ? (a.credite_acordate || 0) : calculateEstimatedCredits(a);
          const crediteB = b.status === 'validat' ? (b.credite_acordate || 0) : calculateEstimatedCredits(b);
          return crediteB - crediteA;
        case 'ore_desc':
          return (b.ore_realizate || 0) - (a.ore_realizate || 0);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const handleExportStudentReport = () => {
    if (!student) return;

    const currentYear = getCurrentSchoolYear();
    const exportDate = new Date().toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const creditCategories = {
      curriculare: creditsByCategory.find(c => c.categorie === 'curricular')?.credite || 0,
      extracurriculare: creditsByCategory.find(c => c.categorie === 'extracurricular')?.credite || 0,
      transferabile: creditsByCategory.find(c => c.categorie === 'transferabil')?.credite || 0
    };

    const totalCredits = creditCategories.curriculare + creditCategories.extracurriculare + creditCategories.transferabile;

    const moduleData = moduleCredits.filter(m => m.total > 0).map(m => ({
      cod: m.cod || '-',
      nume: m.nume || '-',
      tip: m.tip || '-',
      categorie: m.categorie || '-',
      credite: m.total || 0,
      tinta: m.tinta || 0
    }));

    const validatedRequests = requests
      .filter(r => r.status === 'validat')
      .map(r => ({
        activitate: r.activitate || '-',
        modul: getModules(r)?.cod_module || '-',
        tip: getCreditTypes(r)?.nume_tip || '-',
        ore: r.ore_realizate || 0,
        credite: r.credite_acordate || 0,
        data: r.data_activitate ? new Date(r.data_activitate).toLocaleDateString('ro-RO') : '-'
      }));

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1976d2; text-align: center; margin-bottom: 5px; }
          h2 { color: #333; border-bottom: 2px solid #1976d2; padding-bottom: 5px; margin-top: 30px; }
          h3 { color: #555; margin-top: 20px; }
          .header-info { text-align: center; color: #666; margin-bottom: 30px; }
          .info-box { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { margin: 8px 0; }
          .info-label { font-weight: bold; color: #555; display: inline-block; width: 150px; }
          table { border-collapse: collapse; width: 100%; margin-top: 10px; }
          th { background-color: #1976d2; color: white; padding: 12px 8px; text-align: left; border: 1px solid #ddd; }
          td { padding: 10px 8px; border: 1px solid #ddd; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .summary-box { display: flex; gap: 20px; margin: 20px 0; }
          .summary-card { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px; border-radius: 8px; text-align: center; flex: 1; }
          .summary-card.green { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); }
          .summary-card.orange { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
          .summary-card.blue { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
          .summary-value { font-size: 28px; font-weight: bold; }
          .summary-label { font-size: 12px; margin-top: 5px; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
          .no-data { color: #999; font-style: italic; padding: 20px; text-align: center; }
        </style>
      </head>
      <body>
        <h1>Raport Credite Elev</h1>
        <div class="header-info">
          <strong>Anul școlar ${currentYear}</strong><br/>
          Generat la: ${exportDate}
        </div>

        <h2>Informații Elev</h2>
        <div class="info-box">
          <div class="info-row"><span class="info-label">Nume complet:</span> ${student.nume || ''} ${student.prenume || ''}</div>
          <div class="info-row"><span class="info-label">Email:</span> ${student.email || '-'}</div>
          <div class="info-row"><span class="info-label">Clasa:</span> ${student.clasa || '-'}</div>
          <div class="info-row"><span class="info-label">Specializarea:</span> ${student.specializare || '-'}</div>
          <div class="info-row"><span class="info-label">Telefon:</span> ${student.telefon || '-'}</div>
        </div>

        <h2>Sumar Credite</h2>
        <table>
          <tr>
            <th>Categorie</th>
            <th style="text-align: center;">Credite Obținute</th>
          </tr>
          <tr>
            <td>Credite Curriculare</td>
            <td style="text-align: center; font-weight: bold; color: #1976d2;">${creditCategories.curriculare}</td>
          </tr>
          <tr>
            <td>Credite Extracurriculare</td>
            <td style="text-align: center; font-weight: bold; color: #2e7d32;">${creditCategories.extracurriculare}</td>
          </tr>
          <tr>
            <td>Credite Transferabile</td>
            <td style="text-align: center; font-weight: bold; color: #ed6c02;">${creditCategories.transferabile}</td>
          </tr>
          <tr style="background-color: #e3f2fd;">
            <td style="font-weight: bold;">TOTAL CREDITE</td>
            <td style="text-align: center; font-weight: bold; font-size: 18px; color: #1976d2;">${totalCredits}</td>
          </tr>
        </table>

        <h2>Credite pe Module</h2>
        ${moduleData.length > 0 ? `
        <table>
          <tr>
            <th>Cod Modul</th>
            <th>Nume Modul</th>
            <th>Tip Credit</th>
            <th>Categorie</th>
            <th style="text-align: center;">Credite</th>
            <th style="text-align: center;">Țintă</th>
            <th style="text-align: center;">Progres</th>
          </tr>
          ${moduleData.map(m => `
          <tr>
            <td><strong>${m.cod}</strong></td>
            <td>${m.nume}</td>
            <td>${m.tip}</td>
            <td>${m.categorie}</td>
            <td style="text-align: center; font-weight: bold;">${m.credite}</td>
            <td style="text-align: center;">${m.tinta > 0 ? m.tinta : '—'}</td>
            <td style="text-align: center;">${m.tinta > 0 ? Math.min(100, Math.round((m.credite / m.tinta) * 100)) + '%' : '—'}</td>
          </tr>
          `).join('')}
        </table>
        ` : '<p class="no-data">Nu există credite înregistrate pe module.</p>'}

        <h2>Activități Validate</h2>
        ${validatedRequests.length > 0 ? `
        <table>
          <tr>
            <th>Activitate</th>
            <th>Modul</th>
            <th>Tip Credit</th>
            <th style="text-align: center;">Ore</th>
            <th style="text-align: center;">Credite</th>
            <th style="text-align: center;">Data</th>
          </tr>
          ${validatedRequests.map(r => `
          <tr>
            <td>${r.activitate}</td>
            <td>${r.modul}</td>
            <td>${r.tip}</td>
            <td style="text-align: center;">${r.ore}</td>
            <td style="text-align: center; font-weight: bold; color: #2e7d32;">${r.credite}</td>
            <td style="text-align: center;">${r.data}</td>
          </tr>
          `).join('')}
        </table>
        <p style="text-align: right; margin-top: 10px;">
          <strong>Total credite din activități: ${validatedRequests.reduce((sum, r) => sum + r.credite, 0)}</strong>
        </p>
        ` : '<p class="no-data">Nu există activități validate.</p>'}

        <div class="footer">
          <p>Acest raport a fost generat automat din sistemul SRB Credite.</p>
          <p>Pentru întrebări, contactați coordonatorul clasei.</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Raport_${student.nume || ''}_${student.prenume || ''}_${currentYear.replace('/', '-')}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const calculateStats = () => {
    
    const totalCredits = credits.reduce((sum, c) => sum + (c.credite || 0), 0);
    
    const activeModules = credits.filter(c => (c.credite || 0) > 0).length;
    
    const approvedRequests = requests.filter(r => r.status === 'validat').length;
    const pendingRequests = requests.filter(r => r.status === 'in_asteptare').length;
    const rejectedRequests = requests.filter(r => r.status === 'respins').length;
    
    return {
      totalCredits,
      activeModules,
      approvedRequests,
      pendingRequests,
      rejectedRequests,
      totalModules: credits.length,
    };
  };

  const uniqueTypes = [...new Set(requests.map(r => getCreditTypes(r)?.nume_tip).filter(Boolean))];
  const uniqueModules = [...new Set(requests.map(r => getModules(r)?.cod_module).filter(Boolean))];

  const filteredRequests = getFilteredAndSortedRequests();
  const stats = calculateStats();

  if (loading || !student) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" flexDirection="column">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Se încarcă...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header cu buton back */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Tooltip title="Înapoi la listă">
              <IconButton 
                onClick={() => navigate(-1)}
                sx={{ 
                  bgcolor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.main', color: 'white' }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Detalii Elev
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, alignItems: 'center' }}>
            {selectedYear && (
              <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
            )}
            <Tooltip title="Exportă raport pentru părinți (Excel)">
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportStudentReport}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Raport Elev
              </Button>
            </Tooltip>
            <Tooltip title="Reîncarcă datele">
              <IconButton onClick={loadStudentData} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* SECȚIUNEA SUPERIOARĂ: Informații Elev și Statistici */}
      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'primary.main', py: 2, px: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Avatar și Nume */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 70,
                    height: 70,
                    fontSize: 28,
                    bgcolor: 'white',
                    color: 'primary.main',
                    border: '3px solid white',
                    boxShadow: 2,
                  }}
                >
                  {student.prenume?.[0]}{student.nume?.[0]}
                </Avatar>
                <Box sx={{ color: 'white' }}>
                  <Typography variant="h5" fontWeight="bold">
                    {student.nume} {student.prenume}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip
                      label={student.clasa || 'N/A'}
                      size="small"
                      sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }}
                    />
                    {student.specializare && (
                      <Chip
                        label={getSpecializationName(student.specializare)}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            {/* Contact Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                  <Typography variant="body2">{student.email}</Typography>
                </Box>
                {student.telefon && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                    <Typography variant="body2">{student.telefon}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                  <Typography variant="body2">
                    Membru din {new Date(student.created_at).toLocaleDateString('ro-RO')}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Credite Total mare */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <Typography variant="h2" fontWeight="bold">
                  {stats.totalCredits}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  CREDITE TOTALE
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Statistici Grid */}
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' }, display: 'flex' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="h4" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {stats.approvedRequests}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>Cereri Aprobate</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' }, display: 'flex' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fff3e0', borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {stats.pendingRequests}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>În Așteptare</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' }, display: 'flex' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#ffebee', borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="h4" fontWeight="bold" color="error.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {stats.rejectedRequests}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>Respinse</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' }, display: 'flex' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 120 }}>
                <Typography variant="h4" fontWeight="bold" color="info.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {requests.length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>Total Cereri</Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Credite pe categorii - cu spațiere mai bună */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Distribuție Credite pe Categorii
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', justifyContent: 'center' }}>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: '#E3F2FD',
                  border: '2px solid #2196F3',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: 120
                }}
              >
                <Typography variant="h3" fontWeight="bold" color="#1976D2" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {credits.find(c => c.categorie === 'curricular')?.credite || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Curriculare
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: '#F3E5F5',
                  border: '2px solid #9C27B0',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: 120
                }}
              >
                <Typography variant="h3" fontWeight="bold" color="#7B1FA2" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {credits.find(c => c.categorie === 'extracurricular')?.credite || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Extracurriculare
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: '#FFF3E0',
                  border: '2px solid #FF9800',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: 120
                }}
              >
                <Typography variant="h3" fontWeight="bold" color="#F57C00" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                  {credits.find(c => c.categorie === 'transferabil')?.credite || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Transferabile
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* SECȚIUNEA INFERIOARĂ: Tab-uri cu conținut */}
      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            bgcolor: 'grey.100',
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2,
              px: 3,
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 56,
              '&.Mui-selected': {
                bgcolor: 'white',
                color: 'primary.main',
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
            }
          }}
        >
          <Tab label="Module" icon={<CategoryIcon />} iconPosition="start" />
          <Tab label={`Cereri (${requests.length})`} icon={<DescriptionIcon />} iconPosition="start" />
          <Tab label={`Istoric (${schoolYearHistory.length})`} icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: 400 }}>
          {/* Tab 0: Module - Redesigned */}
          {tabValue === 0 && (
            <Box>
              {/* Header cu statistici rapide */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Credite pe Module
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    An școlar {getCurrentSchoolYear()} • {moduleCredits.length} module active
                  </Typography>
                </Box>
                {moduleCredits.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label={`${moduleCredits.reduce((sum, m) => sum + m.total, 0)} Total`}
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                )}
              </Box>

              {moduleCredits.length > 0 ? (
                <>
                  {/* Tabel detaliat pentru module */}
                  <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3 }}>
                    <Table size="small" sx={{ minWidth: 640 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 100 }}>Cod</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Denumire Modul</TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 90 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              
                              <Typography variant="caption" sx={{ color: 'white' }}>Curric.</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 90 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              
                              <Typography variant="caption" sx={{ color: 'white' }}>Extra.</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 90 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              
                              <Typography variant="caption" sx={{ color: 'white' }}>Transf.</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', width: 80 }}>TOTAL</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {moduleCredits.map((mod, index) => (
                          <TableRow 
                            key={index} 
                            hover
                            sx={{ 
                              bgcolor: index % 2 === 0 ? 'white' : 'grey.50',
                              '&:hover': { bgcolor: 'primary.50' }
                            }}
                          >
                            <TableCell>
                              <Chip 
                                label={mod.cod} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {mod.nume}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="body2" 
                                fontWeight="bold" 
                                sx={{ 
                                  color: mod.curricular > 0 ? '#1976D2' : 'text.disabled',
                                  bgcolor: mod.curricular > 0 ? '#E3F2FD' : 'transparent',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  display: 'inline-block'
                                }}
                              >
                                {mod.curricular}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="body2" 
                                fontWeight="bold" 
                                sx={{ 
                                  color: mod.extracurricular > 0 ? '#7B1FA2' : 'text.disabled',
                                  bgcolor: mod.extracurricular > 0 ? '#F3E5F5' : 'transparent',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  display: 'inline-block'
                                }}
                              >
                                {mod.extracurricular}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="body2" 
                                fontWeight="bold" 
                                sx={{ 
                                  color: mod.transferabil > 0 ? '#F57C00' : 'text.disabled',
                                  bgcolor: mod.transferabil > 0 ? '#FFF3E0' : 'transparent',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  display: 'inline-block'
                                }}
                              >
                                {mod.transferabil}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={mod.tinta > 0 ? `${mod.total}/${mod.tinta}` : mod.total}
                                size="small"
                                color={mod.total > 0 ? 'success' : 'default'}
                                sx={{ fontWeight: 'bold', minWidth: 40 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Rând total */}
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell colSpan={2}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              TOTAL GENERAL
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color="#1976D2">
                              {moduleCredits.reduce((sum, m) => sum + m.curricular, 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color="#7B1FA2">
                              {moduleCredits.reduce((sum, m) => sum + m.extracurricular, 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color="#F57C00">
                              {moduleCredits.reduce((sum, m) => sum + m.transferabil, 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={moduleCredits.reduce((sum, m) => sum + m.total, 0)}
                              color="primary"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Carduri sumar vizual */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%', mb: 3 }}>
                    <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
                      <Card sx={{ bgcolor: '#E3F2FD', borderRadius: 2, height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                        <CardContent sx={{ textAlign: 'center', py: 0, p: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h3" fontWeight="bold" color="#1976D2" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {moduleCredits.reduce((sum, m) => sum + m.curricular, 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                            Credite Curriculare
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((moduleCredits.reduce((sum, m) => sum + m.curricular, 0) / Math.max(moduleCredits.reduce((sum, m) => sum + m.total, 0), 1)) * 100, 100)}
                            sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'white' }}
                          />
                        </CardContent>
                      </Card>
                    </Box>
                    <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
                      <Card sx={{ bgcolor: '#F3E5F5', borderRadius: 2, height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                        <CardContent sx={{ textAlign: 'center', py: 0, p: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h3" fontWeight="bold" color="#7B1FA2" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {moduleCredits.reduce((sum, m) => sum + m.extracurricular, 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                            Credite Extracurriculare
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((moduleCredits.reduce((sum, m) => sum + m.extracurricular, 0) / Math.max(moduleCredits.reduce((sum, m) => sum + m.total, 0), 1)) * 100, 100)}
                            sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'white', '& .MuiLinearProgress-bar': { bgcolor: '#9C27B0' } }}
                          />
                        </CardContent>
                      </Card>
                    </Box>
                    <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
                      <Card sx={{ bgcolor: '#FFF3E0', borderRadius: 2, height: '100%', width: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                        <CardContent sx={{ textAlign: 'center', py: 0, p: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h3" fontWeight="bold" color="#F57C00" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {moduleCredits.reduce((sum, m) => sum + m.transferabil, 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                            Credite Transferabile
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((moduleCredits.reduce((sum, m) => sum + m.transferabil, 0) / Math.max(moduleCredits.reduce((sum, m) => sum + m.total, 0), 1)) * 100, 100)}
                            sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'white', '& .MuiLinearProgress-bar': { bgcolor: '#FF9800' } }}
                          />
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <CategoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nu există credite pe module
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Elevul nu are încă credite distribuite pe module în anul școlar curent
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Sugestii pentru elev - vizibile pentru profesori si coordonatori */}
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LightbulbIcon color="primary" />
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Sugestii pentru elev
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Profil recomandat
                  </Typography>
                  {studentProfileRec ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {studentProfileRec.profil}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {studentProfileRec.domenii.map((d) => (
                          <Chip key={d} label={d} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Elevul nu are specializarea completată în profil.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Sugestii de carieră
                  </Typography>
                  {studentCareers.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {studentCareers.slice(0, 4).map((c) => (
                        <Chip
                          key={c.domeniu}
                          label={c.surse.length > 0 ? `${c.domeniu} (${c.surse.join(', ')})` : c.domeniu}
                          size="small"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Fără sugestii de carieră momentan.
                    </Typography>
                  )}
                </Grid>
              </Grid>
              {studentGaps.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Module unde mai are nevoie de credite
                  </Typography>
                  {studentGaps.slice(0, 3).map((gap) => (
                    <Box key={gap.id} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {gap.cod} — {gap.nume}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {gap.total} / {gap.tinta}
                        </Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={gap.progres} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Mai are nevoie de {gap.gap} credite.
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {tabValue === 1 && (
            <Box>
              {/* Header cu statistici cereri */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Cereri Credite
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {requests.length} cereri totale • {requests.filter(r => r.status === 'validat').length} aprobate • {requests.filter(r => r.status === 'in_asteptare').length} în așteptare
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label={`${requests.filter(r => r.status === 'validat').reduce((sum, r) => sum + (r.credite_acordate || 0), 0)} cr. aprobate`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  <Chip 
                    icon={<PendingIcon />}
                    label={`${requests.filter(r => r.status === 'in_asteptare').reduce((sum, r) => sum + (r.credite_solicitate || 0), 0)} cr. în așteptare`}
                    color="warning"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              {/* Card filtre */}
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardHeader 
                  title="Filtrează cererile"
                  avatar={<FilterListIcon color="primary" />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Sortează după"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SortIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        SelectProps={{ native: true }}
                      >
                        <option value="date_desc">Data (cele mai noi)</option>
                        <option value="date_asc">Data (cele mai vechi)</option>
                        <option value="credite_desc">Credite (descrescător)</option>
                        <option value="ore_desc">Ore (descrescător)</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Status"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <MenuItem value="all">Toate statusurile</MenuItem>
                        <MenuItem value="validat">Aprobate</MenuItem>
                        <MenuItem value="in_asteptare">În așteptare</MenuItem>
                        <MenuItem value="respins">Respinse</MenuItem>
                      </TextField>
                    </Grid>
                    
                    {uniqueTypes.length > 0 && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Tip activitate"
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                        >
                          <MenuItem value="all">Toate tipurile</MenuItem>
                          {uniqueTypes.map(type => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    
                    {uniqueModules.length > 0 && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Modul"
                          value={filterModule}
                          onChange={(e) => setFilterModule(e.target.value)}
                        >
                          <MenuItem value="all">Toate modulele</MenuItem>
                          {uniqueModules.map(module => (
                            <MenuItem key={module} value={module}>
                              {module}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                  
                  {/* Indicatori filtre active */}
                  {(filterStatus !== 'all' || filterType !== 'all' || filterModule !== 'all') && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {filterStatus !== 'all' && (
                        <Chip 
                          label={`Status: ${filterStatus === 'validat' ? 'Aprobate' : filterStatus === 'respins' ? 'Respinse' : 'În așteptare'}`}
                          size="small"
                          onDelete={() => setFilterStatus('all')}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {filterType !== 'all' && (
                        <Chip 
                          label={`Tip: ${filterType}`}
                          size="small"
                          onDelete={() => setFilterType('all')}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {filterModule !== 'all' && (
                        <Chip 
                          label={`Modul: ${filterModule}`}
                          size="small"
                          onDelete={() => setFilterModule('all')}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      <Button 
                        size="small" 
                        onClick={() => {
                          setFilterStatus('all');
                          setFilterType('all');
                          setFilterModule('all');
                        }}
                      >
                        Șterge toate filtrele
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
              
              {/* Tabel cereri */}
              <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 500 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' } }}>
                      <TableCell>Activitate</TableCell>
                      <TableCell>Tip</TableCell>
                      <TableCell>Modul</TableCell>
                      <TableCell align="center">Credite</TableCell>
                      <TableCell>Validator</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                          {filteredRequests.map((request) => (
                            <TableRow 
                              key={request.id} 
                              hover
                              sx={{ 
                                '&:hover': { bgcolor: '#f5f9ff' },
                                opacity: request.status === 'respins' ? 0.7 : 1
                              }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {request.nume_activitate}
                                  </Typography>
                                  {request.descriere && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                      {request.descriere.substring(0, 80)}...
                                    </Typography>
                                  )}
                                  {request.ore_realizate && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                      <AccessTimeIcon fontSize="inherit" />
                                      <Typography variant="caption" color="text.secondary">
                                        {request.ore_realizate} ore
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getCreditTypes(request)?.nume_tip || 'N/A'}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${getCreditTypes(request)?.culoare || '#1976d2'}20`,
                                    color: getCreditTypes(request)?.culoare || '#1976d2',
                                    fontWeight: 'bold',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {getModules(request)?.cod_module || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {getModules(request)?.nume_module || ''}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {request.status === 'validat' ? (
                                  <Box>
                                    <Typography variant="h6" fontWeight="bold" color="success.main">
                                      {request.credite_acordate || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {request.ore_realizate} ore × {getPredefinedActivities(request)?.credite_per_ora?.toFixed(2) || '1.00'}
                                    </Typography>
                                  </Box>
                                ) : request.status === 'in_asteptare' ? (
                                  <Box>
                                    <Typography variant="h6" color="warning.main" fontWeight="bold">
                                      {calculateEstimatedCredits(request)}*
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      estimate
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {request.profiles_validator ? (
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {request.profiles_validator.prenume} {request.profiles_validator.nume}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {request.profiles_validator.rol}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Chip 
                                    label="În așteptare" 
                                    size="small" 
                                    variant="outlined" 
                                    color="warning"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={
                                    request.status === 'validat' ? <CheckCircleIcon fontSize="small" /> :
                                    request.status === 'respins' ? <CancelIcon fontSize="small" /> : 
                                    <PendingIcon fontSize="small" />
                                  }
                                  label={
                                    request.status === 'validat' ? 'Aprobată' :
                                    request.status === 'respins' ? 'Respinsă' : 'În așteptare'
                                  }
                                  size="small"
                                  color={
                                    request.status === 'validat' ? 'success' :
                                    request.status === 'respins' ? 'error' : 'warning'
                                  }
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {new Date(request.data_cerere).toLocaleDateString('ro-RO')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(request.data_cerere).toLocaleTimeString('ro-RO', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </Typography>
                                  {request.data_validare && (
                                    <Typography variant="caption" color="success.main" display="block">
                                      Validat: {new Date(request.data_validare).toLocaleDateString('ro-RO')}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {filteredRequests.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nu s-au găsit cereri
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {requests.length === 0 
                      ? 'Acest elev nu are nicio cerere în sistem'
                      : 'Încearcă să schimbi filtrele de căutare'
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              {/* Header istoric */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Istoric Ani Școlari
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Evoluția elevului de-a lungul anilor școlari
                  </Typography>
                </Box>
                {schoolYearHistory.length > 0 && (
                  <Chip 
                    label={`${schoolYearHistory.length} tranziții înregistrate`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
              
              {schoolYearHistory.length > 0 ? (
                <>
                  {/* Timeline vizual */}
                  <Box sx={{ position: 'relative', mb: 3 }}>
                    {schoolYearHistory.map((record, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          display: 'flex', 
                          gap: 3,
                          mb: 2,
                          position: 'relative',
                          '&::before': index < schoolYearHistory.length - 1 ? {
                            content: '""',
                            position: 'absolute',
                            left: 15,
                            top: 40,
                            bottom: -16,
                            width: 2,
                            bgcolor: 'primary.light',
                          } : {}
                        }}
                      >
                        {/* Bullet point */}
                        <Box sx={{ 
                          width: 32, 
                          height: 32, 
                          borderRadius: '50%', 
                          bgcolor: record.motiv === 'promovare' ? 'success.main' : 
                                   record.motiv === 'transfer' ? 'info.main' : 'warning.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                          flexShrink: 0,
                          zIndex: 1,
                        }}>
                          {index + 1}
                        </Box>
                        
                        {/* Card cu detalii */}
                        <Card 
                          sx={{ 
                            flex: 1, 
                            borderRadius: 2,
                            transition: 'all 0.2s',
                            '&:hover': { boxShadow: 3 }
                          }}
                        >
                          <CardContent sx={{ py: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              {/* Info an școlar */}
                              <Grid item xs={12} sm={4}>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                  {record.an_scolar}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  <Chip 
                                    label={record.clasa_veche || 'N/A'}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Typography color="text.secondary">→</Typography>
                                  <Chip 
                                    label={record.clasa_noua || 'N/A'}
                                    size="small"
                                    color="primary"
                                  />
                                </Box>
                                <Chip 
                                  label={record.motiv === 'promovare' ? 'Promovare' : 
                                         record.motiv === 'transfer' ? 'Transfer' : 
                                         record.motiv === 'repetent' ? 'Repetent' : record.motiv}
                                  size="small"
                                  color={record.motiv === 'promovare' ? 'success' : 
                                         record.motiv === 'transfer' ? 'info' : 'warning'}
                                  sx={{ mt: 1 }}
                                />
                              </Grid>
                              
                              {/* Credite */}
                              <Grid item xs={6} sm={4}>
                                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 2 }}>
                                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                                    {record.credite_totale_la_schimbare || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Credite acumulate
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              {/* Credite transferate */}
                              <Grid item xs={6} sm={4}>
                                <Box sx={{ textAlign: 'center', p: 1, bgcolor: record.credite_transferate > 0 ? 'success.50' : 'grey.50', borderRadius: 2 }}>
                                  <Typography variant="h4" fontWeight="bold" color={record.credite_transferate > 0 ? 'success.main' : 'text.secondary'}>
                                    {record.credite_transferate > 0 ? `+${record.credite_transferate}` : '0'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Credite transferate
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            
                            {/* Data schimbării */}
                            <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary">
                                Data schimbării: {new Date(record.data_schimbare).toLocaleDateString('ro-RO', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </Box>
                  
                  {/* Sumar total */}
                  <Card sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Total credite transferate de-a lungul anilor: {schoolYearHistory.reduce((sum, r) => sum + (r.credite_transferate || 0), 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <HistoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nu există istoric
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Elevul nu a fost promovat încă sau nu există schimbări de clasă înregistrate
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Container>
  );
}